import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@frontend/shadcn/components/ui/dialog';
import { Input } from '@frontend/shadcn/components/ui/input';
import { Button } from '@frontend/shadcn/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@frontend/shadcn/components/ui/radio-group';
import { Badge } from '@frontend/shadcn/components/ui/badge';
import { Search, AlertTriangle, ChevronDown } from 'lucide-react';
import { CatalogSelectionRuleConditionOperationType } from '@frontend/applications/ProductFulfillmentApp/types/globalTypes';
import { useGetProductsFromShopify } from '@frontend/app/containers/Settings/ProductFulfillment/Products/hooks/useGetProductsFromShopify';
import { GetProductsFromShopify_getProductsFromShopify_shopifyProducts } from '@frontend/applications/ProductFulfillmentApp/queries/types/GetProductsFromShopify';

interface ShopifyProduct extends GetProductsFromShopify_getProductsFromShopify_shopifyProducts {
  inventory_quantity?: number;
  sellableOnlineQuantity?: number;
  productImgUrl?: string;
}

interface SelectProductDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onProductSelect: (product: ShopifyProduct) => void;
  currentProductTitle: string;
  currencySymbol: string;
}

const SelectProductDialog: React.FC<SelectProductDialogProps> = ({
  isOpen,
  onClose,
  onProductSelect,
  currentProductTitle,
  currencySymbol,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const [expandedProductId, setExpandedProductId] = useState<string | null>(null);
  const [selectedProductData, setSelectedProductData] = useState<{ product: ShopifyProduct; variantId: string } | null>(null);

  // Use the existing product fetch hook
  const {
    fetchShopifyProducts,
    isShopifyProductsLoading,
    shopifyProductsResp,
  } = useGetProductsFromShopify({
    notifyOnNetworkStatusChange: true,
  });

  // Reset states when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSelectedVariantId(null);
      setSelectedProductData(null);
      setExpandedProductId(null);
      fetchShopifyProducts({
        variables: {
          shopifyProductsFilter: {
            query: '',
            isActiveProductOnly: true,
            operation: CatalogSelectionRuleConditionOperationType.OR,
          },
          includeVariants: true,
        },
        fetchPolicy: 'network-only',
      });
    }
  }, [isOpen, fetchShopifyProducts]);

  // Handle search
  const handleSearch = async () => {
    await fetchShopifyProducts({
      variables: {
        shopifyProductsFilter: {
          query: searchQuery,
          isActiveProductOnly: true,
          operation: CatalogSelectionRuleConditionOperationType.OR,
        },
        includeVariants: true,
      },
      fetchPolicy: 'network-only',
    });
  };

  // Handle variant selection - just store the selection
  const handleVariantSelect = (variantId: string, product: ShopifyProduct) => {
    setSelectedVariantId(variantId);
    setSelectedProductData({ product, variantId });
  };

  // Handle save selection
  const handleSave = () => {
    if (selectedProductData) {
      const { product, variantId } = selectedProductData;
      const selectedVariant = product.shopifyProductVariants?.find((v) => v.id === variantId);

      if (selectedVariant) {
        // Create a new product object with the selected variant's information
        const variantProduct = {
          ...product,
          id: selectedVariant.id,
          title: selectedVariant.title,
          imgUrl: selectedVariant.imageUrl || product.imgUrl,
          totalInventory: selectedVariant.quantity,
          shopifyProductVariants: [selectedVariant],
          variantCount: 1,
        };

        // Call the parent's onProductSelect with the prepared data
        onProductSelect(variantProduct);
        onClose();
      }
    }
  };

  // Render stock badge
  const renderStockBadge = (quantity: number) => {
    if (quantity <= 0) {
      return (
        <Badge variant="secondary" className="px-2 py-0.5 bg-[#e5e8eb] text-[#16282d] rounded-lg">
          Out Of Stock
        </Badge>
      );
    }
    return (
      <Badge className="px-2 py-0.5 bg-[#d1f6d0] text-[#16282d] font-normal text-[12px] rounded-lg">
        In Stock - {quantity}
      </Badge>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Select Replacement Product</DialogTitle>
          <p className="text-sm text-gray-500">
            Replacing: {currentProductTitle}
          </p>
        </DialogHeader>

        {/* Search Section */}
        <div className="flex gap-2 pb-4 px-4 border-b">
          <div className="flex-1">
            <Input
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full"
            />
          </div>
          <Button
            onClick={handleSearch}
            disabled={!searchQuery.trim() || isShopifyProductsLoading}
          >
            <Search className="h-4 w-4 mr-2" />
            Search
          </Button>
        </div>

        {/* Products List */}
        <div className="flex-1 overflow-y-auto p-4">
          {isShopifyProductsLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : shopifyProductsResp?.shopifyProducts?.length ? (
            <div className="space-y-4">
              {shopifyProductsResp.shopifyProducts.map((product: ShopifyProduct) => (
                <div key={product.id} className="border rounded-lg overflow-hidden">
                  {/* Product Header - Only show for multi-variant products */}
                  {product.variantCount > 1 && (
                    <div
                      className="p-4 cursor-pointer hover:bg-gray-50"
                      onClick={() => setExpandedProductId(expandedProductId === product.id ? null : product.id)}
                    >
                      <div className="flex items-center gap-4">
                        <img
                          src={product.imgUrl || 'https://storage.googleapis.com/aspireiq-widgets/assets/content_image_placeholder.png'}
                          alt={product.title}
                          className="w-12 h-12 object-cover rounded"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h3 className="font-medium">{product.title}</h3>
                            <ChevronDown
                              className={`h-5 w-5 transition-transform ${
                                expandedProductId === product.id ? 'transform rotate-180' : ''
                              }`}
                            />
                          </div>
                          <p className="text-sm text-gray-500">
                            {product.variantCount} variants
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Variants Section */}
                  {(product.variantCount === 1 || expandedProductId === product.id) && (
                    <div className={product.variantCount > 1 ? 'border-t' : ''}>
                      <RadioGroup
                        value={selectedVariantId}
                        onValueChange={(value) => handleVariantSelect(value, product)}
                        className="p-2"
                      >
                        <div className="space-y-2">
                          {product.shopifyProductVariants.map((variant) => (
                            <div
                              key={variant.id}
                              className="flex items-center gap-4 p-3 rounded-lg hover:bg-gray-50"
                            >
                              <RadioGroupItem value={variant.id} id={variant.id} />
                              <img
                                src={variant.imageUrl || product.imgUrl || 'https://storage.googleapis.com/aspireiq-widgets/assets/content_image_placeholder.png'}
                                alt={variant.title}
                                className="w-10 h-10 object-cover rounded"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-medium">
                                    {product.variantCount === 1 ? product.title : variant.title}
                                  </h4>
                                  {product.variantCount === 1 && (
                                    <p className="text-sm text-gray-500">{currencySymbol}{product.price}</p>
                                  )}
                                </div>
                                {product.variantCount > 1 && (
                                  <p className="text-sm text-gray-500">{currencySymbol}{product.price}</p>
                                )}
                              </div>
                              {renderStockBadge(variant.quantity)}
                            </div>
                          ))}
                        </div>
                      </RadioGroup>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <AlertTriangle className="h-8 w-8 mb-2" />
              <p>No products found</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!selectedVariantId || isShopifyProductsLoading}
          >
            Select
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SelectProductDialog;
